/**
 * SF-3180 Task 3+4 — build the PATCH payload for an SSO config save.
 *
 * Task 3 shipped the minimum viable payload (roleToType + policy +
 * defaultType). Task 4 extends this to cover ALL editable SSO fields
 * (name, domains, clientId, clientSecret, discoveryUrl, scopes,
 * attributeMapping, PingOne metadata, isEnabled) with two guardrails:
 *
 *  1. **Secret-safe** — the masked literal "***" returned by the
 *     auth-service GET is filtered out. If the operator doesn't touch
 *     the client-secret input, the stored secret is preserved server-
 *     side (sf-auth-service PATCH only re-encrypts when `clientSecret`
 *     is present on the body — see SF-3404 route handler).
 *  2. **Change-only payload** — when an existing config is loaded we
 *     diff the form against it and only emit fields that actually
 *     changed. This shrinks the PATCH body, keeps the server audit
 *     log (which records `fields: Object.keys(update)`) accurate, and
 *     reduces the chance of a server 409 (domain overlap check fires
 *     when `domains` is on the update object even if unchanged).
 *
 * `roleToType`, `existingUserRolePolicy`, `existingUserLocationPolicy`,
 * and `defaultType` are always included — the SF-3404 backend replaces
 * `roleToType` wholesale per PATCH and we want the stored policy/
 * defaultType to reflect the operator's explicit choice (see the Task 3
 * rationale below). `existingUserLocationPolicy` (SF-3593) follows the
 * same always-emit contract as `existingUserRolePolicy`.
 */
import type {
  ExistingUserLocationPolicy,
  ExistingUserRolePolicy,
  RoleToTypeRule,
  SsoAttributeMapping,
  SsoConfig,
  SsoProvider,
} from './types';
import { MASKED_SECRET } from './types';
import { parseCommaList } from './validation';

/**
 * Shape the tab owns in React state. Raw strings for comma-separated
 * inputs so the UI can show exactly what the operator typed; we split
 * them here.
 */
export interface SsoPatchFormState {
  // ---- SF-3180 Task 2/3 (always sent) -------------------------------------
  roleToType: RoleToTypeRule[];
  existingUserRolePolicy: ExistingUserRolePolicy;
  existingUserLocationPolicy: ExistingUserLocationPolicy;
  defaultType: string;
  // ---- SF-3180 Task 4 — editable SSO fields -------------------------------
  name: string;
  provider: SsoProvider;
  discoveryUrl: string;
  clientId: string;
  clientSecret: string;
  scopesRaw: string;
  domainsRaw: string;
  pingOneRegion: string;
  pingOneEnvironmentId: string;
  isEnabled: boolean;
  emailClaim: string;
  rolesClaim: string;
  locationsClaim: string;
}

export interface SsoPatchPayload {
  // Task 2/3 (always present)
  roleToType: RoleToTypeRule[];
  existingUserRolePolicy: ExistingUserRolePolicy;
  existingUserLocationPolicy: ExistingUserLocationPolicy;
  defaultType: string;
  // Task 4 — optional: only present when changed vs. the loaded config
  name?: string;
  provider?: SsoProvider;
  discoveryUrl?: string;
  clientId?: string;
  clientSecret?: string;
  scopes?: string[];
  domains?: string[];
  pingOneRegion?: string | null;
  pingOneEnvironmentId?: string | null;
  isEnabled?: boolean;
  attributeMapping?: SsoAttributeMapping | null;
}

function buildAttributeMapping(state: SsoPatchFormState): SsoAttributeMapping | null {
  const out: SsoAttributeMapping = {};
  if (state.emailClaim?.trim()) out.email = state.emailClaim.trim();
  if (state.rolesClaim?.trim()) out.roles = state.rolesClaim.trim();
  if (state.locationsClaim?.trim()) out.locations = state.locationsClaim.trim();
  return Object.keys(out).length > 0 ? out : null;
}

function attributeMappingEquals(
  a: SsoAttributeMapping | null,
  b: SsoAttributeMapping | null,
): boolean {
  if (a === b) return true;
  // NOTE: `employeeId` is intentionally excluded from equality because the
  // form doesn't render or preserve it. If a loaded config has an
  // `employeeId` claim set via the API directly, we leave it alone —
  // `buildAttributeMapping` returns a mapping built from the three fields
  // the UI actually owns (email/roles/locations), so we emit
  // `attributeMapping` only when one of THOSE three differs. Including
  // employeeId here would cause unrelated saves to overwrite/clear it.
  // See GPT-5.5 review warning #6 on SF-3180.
  //
  // Treat null as an empty mapping so that, e.g., a=null vs
  // b={ employeeId: 'emp-123' } (no UI-owned keys on either side) is
  // correctly treated as equal — the old `!a || !b` early return would
  // return `a === b` (false) in that case, triggering a spurious PATCH.
  const aObj = a ?? {};
  const bObj = b ?? {};
  return (
    (aObj.email || null) === (bObj.email || null) &&
    (aObj.roles || null) === (bObj.roles || null) &&
    (aObj.locations || null) === (bObj.locations || null)
  );
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function nullableStringEqual(a: string, b: string | null | undefined): boolean {
  const aVal = a?.trim() || '';
  const bVal = b?.trim?.() || '';
  return aVal === bVal;
}

/**
 * Build the PATCH body for a save.
 *
 * When `loaded` is `null` (create flow — not yet reachable from the UI
 * in Task 4, the tab gates save on the presence of a config) the helper
 * still emits a coherent "all editable fields" payload so the create
 * flow can be wired later without reshaping the contract.
 */
export function buildPatchPayload(
  state: SsoPatchFormState,
  loaded: SsoConfig | null,
): SsoPatchPayload {
  // Always-emit block (Task 2/3 semantics preserved).
  const payload: SsoPatchPayload = {
    roleToType: state.roleToType.map((r) => ({
      match: r.match,
      value: r.value,
      type: r.type,
    })),
    existingUserRolePolicy: state.existingUserRolePolicy,
    existingUserLocationPolicy: state.existingUserLocationPolicy,
    defaultType: state.defaultType,
  };

  const scopes = parseCommaList(state.scopesRaw);
  const domains = parseCommaList(state.domainsRaw).map((d) => d.toLowerCase());
  const attrMapping = buildAttributeMapping(state);

  if (!loaded) {
    // Create flow — include everything. (Tab doesn't expose this path in
    // Task 4 but the payload shape stays aligned with SF-3404 POST.)
    payload.name = state.name.trim();
    payload.provider = state.provider;
    payload.discoveryUrl = state.discoveryUrl.trim();
    payload.clientId = state.clientId.trim();
    if (state.clientSecret && state.clientSecret !== MASKED_SECRET) {
      payload.clientSecret = state.clientSecret;
    }
    if (scopes.length > 0) payload.scopes = scopes;
    payload.domains = domains;
    payload.isEnabled = !!state.isEnabled;
    if (attrMapping) payload.attributeMapping = attrMapping;
    if (state.provider === 'pingone') {
      payload.pingOneRegion = state.pingOneRegion.trim() || null;
      payload.pingOneEnvironmentId = state.pingOneEnvironmentId.trim() || null;
    }
    return payload;
  }

  // Edit flow — diff against loaded.
  const trimmedName = state.name.trim();
  if (trimmedName !== (loaded.name || '')) payload.name = trimmedName;

  // Provider is immutable after creation (SF-3404 blocker fix). The UI
  // disables the Select on existing configs, but we still belt-and-brace
  // here so a stale form state can't leak a `provider` field into a PATCH
  // body — the backend would 400 on mismatch anyway, but silently
  // dropping it from the payload keeps benign no-op saves working.
  // Note: we intentionally do NOT emit provider on edit, even when unchanged.

  const trimmedUrl = state.discoveryUrl.trim();
  if (trimmedUrl !== (loaded.discoveryUrl || '')) payload.discoveryUrl = trimmedUrl;

  const trimmedClientId = state.clientId.trim();
  if (trimmedClientId !== (loaded.clientId || '')) payload.clientId = trimmedClientId;

  // Secret-safe: only send when the operator typed a non-masked value.
  // Empty input ⇒ preserve (operator didn't touch it).
  if (state.clientSecret && state.clientSecret !== MASKED_SECRET) {
    payload.clientSecret = state.clientSecret;
  }

  if (!arraysEqual(scopes, loaded.scopes || [])) payload.scopes = scopes;
  if (
    !arraysEqual(
      domains,
      (loaded.domains || []).map((d) => d.toLowerCase()),
    )
  ) {
    payload.domains = domains;
  }

  if (state.isEnabled !== loaded.isEnabled) payload.isEnabled = state.isEnabled;

  if (!attributeMappingEquals(attrMapping, loaded.attributeMapping)) {
    // Preserve any unknown fields the backend set (e.g. `employeeId`)
    // that the UI doesn't render — merge the UI-owned keys on top.
    const merged: SsoAttributeMapping = {
      ...(loaded.attributeMapping ?? {}),
      ...(attrMapping ?? {}),
    };
    // Drop the three UI-owned keys when the form cleared them, so the
    // caller can explicitly remove a claim.
    if (!attrMapping?.email) delete merged.email;
    if (!attrMapping?.roles) delete merged.roles;
    if (!attrMapping?.locations) delete merged.locations;
    payload.attributeMapping = Object.keys(merged).length > 0 ? merged : null;
  }

  // PingOne region/envId only diff when provider is (or stays) pingone.
  if (state.provider === 'pingone') {
    if (!nullableStringEqual(state.pingOneRegion, loaded.pingOneRegion)) {
      payload.pingOneRegion = state.pingOneRegion.trim() || null;
    }
    if (!nullableStringEqual(state.pingOneEnvironmentId, loaded.pingOneEnvironmentId)) {
      payload.pingOneEnvironmentId = state.pingOneEnvironmentId.trim() || null;
    }
  }

  return payload;
}
